import dayjs from "dayjs";
import { useState } from "react";
import styled from "styled-components";

import { ErrorComponent } from "@components/ErrorComponent";
import { AppNav } from "@components/Header/AppNav";
import { Loader } from "@components/Loader";
import { REFERRAL_TERMS_AND_CONDITIONS_URL } from "@constants";
import { DateFormat, UserReferral, UserReferralStatus } from "@ctypes";
import { useReferralData, useTheme } from "@hooks";
import { Translations, i18n } from "@i18n";
import {
  ArtPanel,
  AvatarCircle,
  Card,
  Content,
  Eyebrow,
  FilledButton,
  GhostButton,
  Grain,
  Page,
  SerifTitle,
} from "@routes/DashboardRoute/Home/styles";
import { home, homeCssVars } from "@routes/DashboardRoute/Home/tokens";
import { convertCentsToDollar, formatCurrency, getFullName, getReferralStatus } from "@utils";

const COLLAPSED_COUNT = 6;

const Hero = styled.div`
  padding: 28px ${home.pagePad} 12px;
  display: flex;
  flex-direction: column;
  gap: 34px;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 640px;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: ${home.ink58};
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 396px;
  gap: 20px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LinkCard = styled(Card)`
  padding: 24px 26px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const LinkLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${home.ink44};
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const LinkField = styled.div`
  flex: 1;
  min-width: 220px;
  height: 46px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid ${home.line12};
  background: ${home.fill04};
  font-size: 14px;
  color: ${home.ink72};
  overflow: hidden;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StepsCard = styled(Card)`
  padding: 24px 26px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const StepNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid ${home.line18};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: ${home.ink72};
`;

const StepTitle = styled.div`
  font-family: ${home.serif};
  font-size: 18px;
  color: ${home.ink};
`;

const StepBody = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: ${home.ink52};
`;

/* Painted earnings panel – the branding surface that replaces the
   mobile gift illustration. */
const EarningsPanel = styled(ArtPanel)`
  background:
    radial-gradient(460px 340px at 92% 2%, rgba(224, 155, 124, 0.58), transparent 66%),
    radial-gradient(440px 400px at 2% 102%, rgba(143, 191, 176, 0.26), transparent 66%),
    radial-gradient(320px 300px at 44% 46%, rgba(176, 104, 84, 0.32), transparent 70%),
    linear-gradient(158deg, #2a2129 0%, #130f16 100%);
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EarnedAmount = styled.div`
  font-family: ${home.serif};
  font-size: 52px;
  line-height: 0.96;
  letter-spacing: -1.2px;
`;

const PendingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${home.ink66};

  b {
    font-weight: 600;
    color: ${home.copper};
  }
`;

const PanelDivider = styled.div`
  height: 1px;
  background: ${home.line10};
  margin: 6px 0;
`;

const PanelNote = styled.div`
  font-size: 12px;
  line-height: 1.6;
  color: ${home.ink52};

  a {
    color: ${home.copper};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ReferralsSection = styled.div`
  padding: 56px ${home.pagePad} 72px;
  display: flex;
  flex-direction: column;
  gap: 26px;
`;

const ListCard = styled(Card)`
  padding: 8px 26px;
  max-width: 760px;
`;

const ReferralRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 0;
  border: none;
  background: none;
  font-family: ${home.sans};
  color: ${home.ink};
  text-align: left;
  cursor: pointer;

  &:not(:first-child) {
    border-top: 1px solid ${home.line08};
  }
`;

const ReferralName = styled.div`
  font-family: ${home.serif};
  font-size: 19px;
`;

const ReferralMeta = styled.div`
  font-size: 12px;
  color: ${home.ink52};
  margin-top: 3px;
`;

const ProgressDots = styled.div`
  display: flex;
  gap: 5px;
  margin-left: auto;

  span {
    width: 22px;
    height: 4px;
    border-radius: 999px;
    background: ${home.fill10};

    &.done {
      background: ${home.copper};
    }
  }
`;

const Chevron = styled.svg<{ $open: boolean }>`
  flex-shrink: 0;
  transition: transform 180ms ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const Timeline = styled.div`
  padding: 2px 0 20px 60px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TimelineStep = styled.div<{ $done: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  color: ${({ $done }) => ($done ? home.ink : home.ink44)};

  .marker {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 1px;
    border-radius: 999px;
    border: 1px solid ${({ $done }) => ($done ? home.sage : home.line18)};
    background: ${({ $done }) => ($done ? home.sage : "transparent")};
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .date {
    font-size: 12px;
    color: ${home.ink44};
    margin-top: 3px;
  }
`;

const EmptyBody = styled.div`
  font-size: 14px;
  color: ${home.ink52};
  padding: 20px 0;
`;

const referralSteps = (referral: UserReferral | null) => [
  {
    label: i18n.t(Translations.ReferralDashboardRouteStepSignUp),
    completedAt: referral?.signedUpAt,
  },
  {
    label: i18n.t(Translations.ReferralDashboardRouteStepVerified),
    completedAt: referral?.activatedAt,
  },
  {
    label: i18n.t(Translations.ReferralDashboardRouteStepDeposited),
    completedAt: referral?.status === UserReferralStatus.Canceled ? null : referral?.contributedAt,
  },
];

export const ReferralDashboardRoute = () => {
  const { theme } = useTheme();

  const [copied, setCopied] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const { data, referralAmount, loading, error } = useReferralData();

  const shareUrl = data?.referralUrl;

  const handleCopy = () => {
    if (!shareUrl) return;

    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!shareUrl) return;

    const message = data?.inviteText ? `${data.inviteText}\n\n${shareUrl}` : shareUrl;

    if (navigator.share) {
      try {
        await navigator.share({ text: message });
        return;
      } catch {
        // Dismissed or unsupported payload – fall back to copying.
      }
    }

    handleCopy();
  };

  const steps = [
    {
      title: i18n.t(Translations.ReferralDashboardRouteHowItWorksItem1Title),
      body: i18n.t(Translations.ReferralDashboardRouteHowItWorksItem1Subtitle),
    },
    {
      title: i18n.t(Translations.ReferralDashboardRouteHowItWorksItem2Title, {
        amount: referralAmount,
      }),
      body: i18n.t(Translations.ReferralDashboardRouteHowItWorksItem2Subtitle, {
        amount: referralAmount,
      }),
    },
    {
      title: i18n.t(Translations.ReferralDashboardRouteHowItWorksItem3Title, {
        amount: referralAmount,
      }),
      body: i18n.t(Translations.ReferralDashboardRouteHowItWorksItem3Subtitle, {
        amount: referralAmount,
      }),
    },
  ];

  const referrals = data?.referrals ?? [];
  const visibleReferrals = showAll ? referrals : referrals.slice(0, COLLAPSED_COUNT);

  return (
    <Page style={homeCssVars(theme.meta.dark)}>
      <Grain aria-hidden="true" />
      <Content>
        <AppNav />

        {loading ? (
          <Hero>
            <Loader />
          </Hero>
        ) : error || !shareUrl ? (
          <Hero>
            <ErrorComponent />
          </Hero>
        ) : (
          <>
            <Hero>
              <Intro>
                <Eyebrow>{i18n.t(Translations.ReferralDashboardRouteEyebrow)}</Eyebrow>
                <SerifTitle as="h1" $size={46} style={{ margin: 0 }}>
                  {i18n.t(Translations.ReferralDashboardRouteTitleLead)} <em>{referralAmount}</em>{" "}
                  {i18n.t(Translations.ReferralDashboardRouteTitleTail)}
                </SerifTitle>
                <Subtitle>
                  {i18n.t(Translations.ReferralDashboardRouteSubtitle, {
                    amount: referralAmount,
                  })}
                </Subtitle>
              </Intro>

              <HeroGrid>
                <HeroLeft>
                  <LinkCard>
                    <LinkLabel>{i18n.t(Translations.ReferralDashboardRouteLinkLabel)}</LinkLabel>
                    <LinkRow>
                      <LinkField title={shareUrl}>
                        <span>{shareUrl}</span>
                      </LinkField>
                      <FilledButton onClick={handleCopy}>
                        {copied
                          ? i18n.t(Translations.ReferralDashboardRouteCopied)
                          : i18n.t(Translations.ReferralDashboardRouteCopyCTA)}
                      </FilledButton>
                      <GhostButton onClick={handleShare}>
                        {i18n.t(Translations.ReferralDashboardRouteShareCTA)}
                      </GhostButton>
                    </LinkRow>
                  </LinkCard>

                  <StepsCard>
                    {steps.map((step, index) => (
                      <Step key={step.title}>
                        <StepNumber>{index + 1}</StepNumber>
                        <StepTitle>{step.title}</StepTitle>
                        <StepBody>{step.body}</StepBody>
                      </Step>
                    ))}
                  </StepsCard>
                </HeroLeft>

                <EarningsPanel>
                  <Eyebrow>{i18n.t(Translations.ReferralDashboardRouteEarnedTitle)}</Eyebrow>
                  <EarnedAmount>
                    {formatCurrency(convertCentsToDollar(data?.earnedBonusInCents))}
                  </EarnedAmount>
                  <PendingRow>
                    {i18n.t(Translations.ReferralDashboardRoutePendingTitle)}
                    <b>{formatCurrency(convertCentsToDollar(data?.pendingBonusInCents))}</b>
                  </PendingRow>
                  <PanelDivider />
                  <PanelNote>
                    {i18n.t(Translations.ReferralDashboardRouteEarningsDescription)}{" "}
                    <a
                      href={REFERRAL_TERMS_AND_CONDITIONS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {i18n.t(Translations.ReferralDashboardRouteTermsAndConditions)}
                    </a>
                  </PanelNote>
                </EarningsPanel>
              </HeroGrid>
            </Hero>

            <ReferralsSection>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Eyebrow>{i18n.t(Translations.ReferralDashboardRouteYourReferralsTitle)}</Eyebrow>
                <SerifTitle>
                  {i18n.t(Translations.ReferralDashboardRouteReferralsLead)}{" "}
                  <em>{i18n.t(Translations.ReferralDashboardRouteReferralsHighlight)}</em>.
                </SerifTitle>
              </div>

              <ListCard>
                {referrals.length === 0 ? (
                  <EmptyBody>{i18n.t(Translations.ReferralDashboardRouteNoReferrals)}</EmptyBody>
                ) : (
                  visibleReferrals.map((referral, index) => {
                    const fullName = getFullName(referral?.firstName, referral?.lastName);
                    const name =
                      fullName.length > 0
                        ? fullName
                        : i18n.t(Translations.ReferralDashboardRouteDefaultName);

                    const { current, total } = getReferralStatus(referral);

                    // A canceled referral never completes its final step.
                    const shownCurrent =
                      referral?.status === UserReferralStatus.Canceled && current === total
                        ? total - 1
                        : current;

                    const isOpen = expandedIndex === index;

                    return (
                      <div key={`referral-${index}`}>
                        <ReferralRow
                          onClick={() => setExpandedIndex(isOpen ? null : index)}
                          aria-expanded={isOpen}
                        >
                          <AvatarCircle $tone={index % 2 === 0 ? "copper" : "sage"}>
                            {name.charAt(0).toUpperCase()}
                          </AvatarCircle>
                          <div>
                            <ReferralName>{name}</ReferralName>
                            <ReferralMeta>
                              {i18n.t(Translations.ReferralDashboardRouteItemStatus, {
                                current: shownCurrent,
                                total,
                              })}
                            </ReferralMeta>
                          </div>
                          <ProgressDots aria-hidden="true">
                            {Array.from({ length: total }, (_, dot) => (
                              <span key={dot} className={dot < shownCurrent ? "done" : ""} />
                            ))}
                          </ProgressDots>
                          <Chevron
                            $open={isOpen}
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            style={{ stroke: home.ink44 }}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </Chevron>
                        </ReferralRow>

                        {isOpen && (
                          <Timeline>
                            {referralSteps(referral).map((step) => (
                              <TimelineStep key={step.label} $done={Boolean(step.completedAt)}>
                                <span className="marker">
                                  {step.completedAt && (
                                    <svg
                                      width="9"
                                      height="9"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#10141C"
                                      strokeWidth="3.4"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="m4 12.5 5.5 5.5L20 6.5" />
                                    </svg>
                                  )}
                                </span>
                                <div>
                                  {step.label}
                                  {step.completedAt && (
                                    <div className="date">
                                      {i18n.t(Translations.ReferralDashboardRouteStepCompleted, {
                                        date: dayjs(step.completedAt).format(DateFormat.FullDate),
                                      })}
                                    </div>
                                  )}
                                </div>
                              </TimelineStep>
                            ))}
                          </Timeline>
                        )}
                      </div>
                    );
                  })
                )}
              </ListCard>

              {referrals.length > COLLAPSED_COUNT && !showAll && (
                <GhostButton style={{ alignSelf: "flex-start" }} onClick={() => setShowAll(true)}>
                  {i18n.t(Translations.ReferralDashboardRouteSeeAll)}
                </GhostButton>
              )}
            </ReferralsSection>
          </>
        )}
      </Content>
    </Page>
  );
};
